import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveAssetUrl } from "@/lib/api";
import { Section } from "@/components/Section";
import { CheckCircle2, ArrowRight, Check, Calculator } from "lucide-react";
import useShareAttribution from "@/lib/useShareAttribution";
import { trackEvent } from "@/lib/analytics";

const CLINIC_TYPES = [
  "Primary Care",
  "Wellness Clinic",
  "Med Spa",
  "Concierge Practice",
  "Functional Medicine",
  "Other",
];

const PROGRAM_OPTIONS = ["Sleep Health", "Metabolic Health", "Hormone & Longevity", "Diagnostics & Testing", "Wellness & Recovery"];

function MarketingVideoEmbed() {
  const [pub, setPub] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/marketing-video`)
      .then((r) => r.json())
      .then((d) => setPub(d || {}))
      .catch(() => {});
  }, []);

  if (!pub || !pub.available || !pub.url) return null;

  return (
    <Section testId="marketing-video-embed" className="pt-28 pb-0">
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-3">
          The platform in 2 minutes
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
          Operational infrastructure for cash-pay healthcare.
        </h2>
        <p className="text-slate-600 mt-3 max-w-xl mx-auto text-sm leading-relaxed">
          A short pitch on what Nodera Health does — and the operational
          gap it closes for modern clinics.
        </p>
      </div>
      <div className="rounded-3xl overflow-hidden bg-slate-900 max-w-5xl mx-auto shadow-2xl">
        <video
          key={pub.url}
          data-testid="marketing-video-player"
          src={resolveAssetUrl(pub.url)}
          controls
          playsInline
          className="w-full aspect-video bg-slate-900"
        />
      </div>
    </Section>
  );
}

export default function Demo() {
  useShareAttribution();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    work_email: "",
    clinic_name: "",
    clinic_type: "",
    role: "",
    team_size: "",
    interested_programs: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [modelSummary, setModelSummary] = useState(null);

  // Prefill from ROI calculator query params
  useEffect(() => {
    const conversations = searchParams.get("conversations");
    const conversion = searchParams.get("conversion");
    const price = searchParams.get("price");
    const vendor = searchParams.get("vendor_cost");
    const platform = searchParams.get("platform_cost");
    const netMo = searchParams.get("net_monthly");
    const netYr = searchParams.get("net_annualized");

    if (!conversations && !price) return;

    const usd = (n) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n));

    const summary = {
      conversations,
      conversion,
      price: price ? usd(price) : null,
      vendor: vendor ? usd(vendor) : null,
      platform: platform ? usd(platform) : null,
      netMo: netMo ? usd(netMo) : null,
      netYr: netYr ? usd(netYr) : null,
    };
    setModelSummary(summary);

    const lines = [
      "ROI calculator assumptions carried over:",
      `• Eligible patient conversations / month: ${conversations || "—"}`,
      `• Conversion rate: ${conversion ? `${conversion}%` : "—"}`,
      `• Avg patient cash-pay price: ${summary.price || "—"}`,
      `• Nodera contracted vendor cost per service: ${summary.vendor || "—"}`,
      `• Monthly platform cost: ${summary.platform || "—"}`,
      `• Estimated net contribution: ${summary.netMo || "—"} / mo (${summary.netYr || "—"} / yr)`,
      "",
      "Note: figures are illustrative planning assumptions, not commitments.",
    ];
    setForm((f) => ({ ...f, message: lines.join("\n") }));
  }, [searchParams]);


  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const togglePrg = (p) => {
    setForm((f) => ({
      ...f,
      interested_programs: f.interested_programs.includes(p)
        ? f.interested_programs.filter((x) => x !== p)
        : [...f.interested_programs, p],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.work_email || !form.clinic_name || !form.clinic_type) {
      toast.error("Please complete the required fields.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form };
      if (modelSummary) {
        payload.roi_model = {
          conversations: Number(searchParams.get("conversations") || 0),
          conversion_pct: Number(searchParams.get("conversion") || 0),
          price_usd: Number(searchParams.get("price") || 0),
          vendor_cost_usd: Number(searchParams.get("vendor_cost") || 0),
          platform_cost_usd: Number(searchParams.get("platform_cost") || 0),
          net_monthly_usd: Number(searchParams.get("net_monthly") || 0),
          net_annualized_usd: Number(searchParams.get("net_annualized") || 0),
        };
      }
      // Carry UTM through to the demo request so the admin share-attribution
      // panel can compute hits → conversions per channel.
      const utmSource = searchParams.get("utm_source");
      if (utmSource) {
        payload.utm_source = utmSource;
        payload.utm_medium = searchParams.get("utm_medium") || "share";
        payload.utm_campaign = searchParams.get("utm_campaign") || null;
      }
      await api.post("/demo", payload);
      setSuccess(true);
      toast.success("Request received. Our team will reach out shortly.");
      trackEvent("demo_form_submitted", {
        utm_source: payload.utm_source || null,
        utm_medium: payload.utm_medium || null,
        utm_campaign: payload.utm_campaign || null,
        clinic_type: payload.clinic_type || null,
      });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Section className="pt-32" testId="demo-success">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-bold text-slate-900">Request received.</h1>
          <p className="mt-3 text-slate-600">
            We'll be in touch within one business day to schedule your walkthrough of the platform.
          </p>
        </div>
      </Section>
    );
  }

  return (
    <div data-testid="demo-page">
      <MarketingVideoEmbed />
      <Section className="pt-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">Book a demo</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
              See the operating layer behind cash-pay clinic growth.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              A 30-minute session with our team walking through curated programs, workflow automation, vendor coordination, and recurring revenue infrastructure.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Walkthrough of the curated program catalog",
                "Live tour of workflow automation",
                "Review of financial workflow infrastructure",
                "Custom deployment plan for your clinic",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-slate-700">
                  <span className="w-5 h-5 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="demo-form"
              className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 space-y-5 shadow-sm"
            >
              {modelSummary && (
                <div
                  data-testid="demo-roi-banner"
                  className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 flex items-start gap-3"
                >
                  <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
                    <Calculator className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-700">
                      ROI model carried over
                    </div>
                    <p className="mt-1 text-sm text-slate-700 leading-relaxed">
                      Your assumptions ({modelSummary.conversations} conversations · {modelSummary.conversion}% · {modelSummary.price}) — estimated <span className="font-semibold text-slate-900">{modelSummary.netMo}/mo</span> ({modelSummary.netYr}/yr) — will be shared with our team.
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">Illustrative planning figures only.</p>
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First name" required>
                  <input
                    required
                    data-testid="demo-first-name-input"
                    value={form.first_name}
                    onChange={handle("first_name")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Last name" required>
                  <input
                    required
                    data-testid="demo-last-name-input"
                    value={form.last_name}
                    onChange={handle("last_name")}
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Work email" required>
                <input
                  type="email"
                  required
                  data-testid="demo-email-input"
                  value={form.work_email}
                  onChange={handle("work_email")}
                  className={inputCls}
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Clinic name" required>
                  <input
                    required
                    data-testid="demo-clinic-input"
                    value={form.clinic_name}
                    onChange={handle("clinic_name")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Clinic type" required>
                  <select
                    required
                    data-testid="demo-clinic-type-select"
                    value={form.clinic_type}
                    onChange={handle("clinic_type")}
                    className={inputCls}
                  >
                    <option value="">Select a type</option>
                    {CLINIC_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your role">
                  <input
                    data-testid="demo-role-input"
                    value={form.role}
                    onChange={handle("role")}
                    className={inputCls}
                    placeholder="e.g. Clinic Owner"
                  />
                </Field>
                <Field label="Team size">
                  <select
                    data-testid="demo-team-size-select"
                    value={form.team_size}
                    onChange={handle("team_size")}
                    className={inputCls}
                  >
                    <option value="">Select</option>
                    <option>1–5</option>
                    <option>6–20</option>
                    <option>21–50</option>
                    <option>51+</option>
                  </select>
                </Field>
              </div>

              <Field label="Programs you're interested in">
                <div className="flex flex-wrap gap-2">
                  {PROGRAM_OPTIONS.map((p) => {
                    const active = form.interested_programs.includes(p);
                    return (
                      <button
                        type="button"
                        key={p}
                        data-testid={`demo-program-${p.replace(/\s+|&/g, "-").toLowerCase()}`}
                        onClick={() => togglePrg(p)}
                        className={`px-3.5 py-2 rounded-full text-xs font-medium border transition-colors ${
                          active
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="What would you like to cover?">
                <textarea
                  data-testid="demo-message-input"
                  value={form.message}
                  onChange={handle("message")}
                  rows={4}
                  className={`${inputCls} resize-y`}
                  placeholder="Share context about your clinic or programs you're exploring."
                />
              </Field>

              <button
                type="submit"
                data-testid="demo-submit-btn"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {submitting ? "Submitting…" : (<>Request demo <ArrowRight className="w-4 h-4" /></>)}
              </button>

              <p className="text-[11px] text-slate-500 text-center">
                By submitting, you agree we may contact you about Nodera Health.
              </p>
            </form>
          </div>
        </div>
      </Section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-blue-600">*</span>}
      </span>
      {children}
    </label>
  );
}
